import { MessageTranslationState } from './message-translation-selectors';
import { FAILURE, STARTING, STREAM, SUCCESS } from '../action/util/action-name-creators';
import { Action, StateStatus, updateResourceByParam } from '../reducers/utils/state-manager';

const InitialState = {
  messageTranslationsById: {},
  messageTranslationsStateByMessageId: {},
  messageTranslationsStateByContactId: {},
} as MessageTranslationState;

type PayloadArgs = { messageTranslation: MessageTranslationRequest };
type PayloadData = { key: 'messageBody' | 'subject'; value: string };

export const messageTranslationReducer = (
  state: MessageTranslationState = InitialState,
  action: Action,
) => {
  switch (action.type) {
    case STARTING('TRANSLATE_MESSAGE'): {
      const {
        messageTranslation: { messageId },
      } = action.payload.args as PayloadArgs;

      if (!messageId) {
        return state;
      }

      return updateResourceByParam(
        state,
        'messageTranslations',
        'messageId',
        messageId.toString(),
        true,
      );
    }
    case STREAM('TRANSLATE_MESSAGE'): {
      const {
        args: {
          messageTranslation: { messageId, languageCode },
        },
        data: { key, value },
      } = action.payload as { args: PayloadArgs; data: PayloadData };
      if (!messageId) {
        return state;
      }

      const messageTranslations = state.messageTranslationsById[messageId] ?? {};
      const messageTranslation = messageTranslations[languageCode] ?? {
        messageBody: undefined,
        subject: undefined,
        languageCode,
        messageId,
      };

      const updatedTranslationField = (messageTranslation[key] || '') + value;

      // we're not using typical state manager functions here because we
      // have a unique need for updating state while streaming.
      // we want to add new data from the request but maintain a loading state
      return {
        ...state,
        messageTranslationsById: {
          ...state.messageTranslationsById,
          [messageId]: {
            ...messageTranslations,
            [languageCode]: {
              ...messageTranslation,
              [key]: updatedTranslationField,
            },
          },
        },
        messageTranslationsStateByMessageId: {
          ...state.messageTranslationsStateByMessageId,
          [messageId]: StateStatus.loading,
        },
      };
    }
    case SUCCESS('TRANSLATE_MESSAGE'): {
      const {
        args: {
          messageTranslation: { messageId },
        },
      } = action.payload as { args: PayloadArgs };
      return {
        ...state,
        messageTranslationsStateByMessageId: {
          ...state.messageTranslationsStateByMessageId,
          [messageId]: StateStatus.success,
        },
      };
    }
    case FAILURE('TRANSLATE_MESSAGE'): {
      const {
        messageTranslation: { messageId },
      } = action.payload.args as PayloadArgs;
      return updateResourceByParam(
        state,
        'messageTranslations',
        'messageId',
        messageId.toString(),
        false,
      );
    }
    case STARTING('LOAD_CONTACT_MESSAGE_TRANSLATIONS'): {
      const { contactId } = action.payload.args;
      return updateResourceByParam(state, 'messageTranslations', 'contactId', contactId, true);
    }
    case SUCCESS('LOAD_CONTACT_MESSAGE_TRANSLATIONS'): {
      const {
        args: { contactId },
        data,
      } = action.payload;

      const loadedMessageTranslationsById = {} as typeof state.messageTranslationsById;
      const loadedMessageTranslationsStateByMessageId =
        {} as typeof state.messageTranslationsStateByMessageId;
      data.forEach((messageTranslation: MessageTranslation) => {
        if (!loadedMessageTranslationsById[messageTranslation.messageId]) {
          loadedMessageTranslationsById[messageTranslation.messageId] = {
            [messageTranslation.languageCode]: messageTranslation,
          };
        } else {
          loadedMessageTranslationsById[messageTranslation.messageId][
            messageTranslation.languageCode
          ] = messageTranslation;
        }
        loadedMessageTranslationsStateByMessageId[messageTranslation.messageId] =
          StateStatus.success;
      });

      return {
        ...state,
        messageTranslationsById: {
          ...state.messageTranslationsById,
          ...loadedMessageTranslationsById,
        },
        messageTranslationsStateByMessageId: {
          ...state.messageTranslationsStateByMessageId,
          ...loadedMessageTranslationsStateByMessageId,
        },
        messageTranslationsStateByContactId: {
          ...state.messageTranslationsStateByContactId,
          [contactId]: StateStatus.success,
        },
      };
    }
    case FAILURE('LOAD_CONTACT_MESSAGE_TRANSLATIONS'): {
      const { contactId } = action.payload.args;
      return updateResourceByParam(state, 'messageTranslations', 'contactId', contactId, false);
    }
    default: {
      return state;
    }
  }
};
